import { z } from 'zod';
import { nullStr, nullArr, nullObj } from 'schemas/nullable';

const zoneRefSchema = z
  .object({
    _id: z.string().default(''),
    type: z.string().default(''),
    code: z.string().default(''),
    color: z.string().default(''),
  })
  .nullable()
  .default(null);

const locationRefSchema = z
  .object({
    _id: z.string().default(''),
    code: z.string().default(''),
    type: z.string().default(''),
    zoneId: zoneRefSchema,
  })
  .nullable()
  .default(null);

const productRefSchema = z
  .object({
    _id: z.string().default(''),
    p_sku: z.string().default(''),
    p_title: z.string().default(''),
    p_images: z
      .object({
        display_image: z.string(),
      })
      .nullable()
      .default(null),
  })
  .nullable()
  .default(null);

const organizationRefSchema = z
  .object({
    _id: z.string(),
    name: z.string(),
  })
  .nullable()
  .default(null);

const warehouseRefSchema = z
  .object({
    _id: z.string().default(''),
    name: z.string().default(''),
  })
  .nullable()
  .default(null);

const cartonAttributesSchema = z
  .object({
    alias: z.string().default(''),
  })
  .default({
    alias: '',
  });

const inventoryItemRefSchema = z
  .object({
    _id: z.string().default(''),
    inventoryType: z.string().default(''),
    lotNumber: nullStr(),
    cartonAttributes: cartonAttributesSchema.optional(),
  })
  .nullable()
  .default(null);

const suggestedTransferMetadataSchema = nullObj({
  lastTriggerOrderId: nullStr(),
  lastTriggeredAt: nullStr(),
  triggerOrderIds: nullArr(z.string()),
});

const transferAlertSchema = z
  .object({
    code: nullStr(),
    message: nullStr(),
  })
  .nullable()
  .default(null);

export const suggestedTransferItemSchema = z.object({
  _id: z.string().default(''),
  status: z.string().default(''),
  priority: z.string().default(''),
  warehouseId: warehouseRefSchema,
  organizationId: organizationRefSchema,
  productId: productRefSchema,
  sourceLocationId: locationRefSchema,
  destinationLocationId: locationRefSchema,
  inventoryId: inventoryItemRefSchema,
  suggestedQuantity: z.number().default(0),
  suggestedQuantityCartons: z.number().nullable().default(null),
  referenceType: z.string().default(''),
  __v: z.number().optional().default(0),
  createdAt: z.string().default(''),
  updatedAt: z.string().default(''),
  expiresAt: z.string().optional().default(''),
  metadata: suggestedTransferMetadataSchema,
  alert: transferAlertSchema,
});

export const inventorySuggestedTransfersListResponseSchema = z.object({
  data: z.array(suggestedTransferItemSchema),
  page: z.number(),
  limit: z.number(),
  total: z.number(),
  totalPages: z.number(),
});

export const suggestedTransferListFilterSchema = z.object({
  limit: z.number(),
  page: z.number(),
  search: z.string().optional(),
  hasUnassignedAllocations: z.boolean().optional(),
  sort: z
    .object({
      sortBy: z.literal('suggestedQuantity'),
      sortOrder: z.string(),
    })
    .optional(),
});

export type InventorySuggestedTransferItem = z.infer<typeof suggestedTransferItemSchema>;
export type InventorySuggestedTransfersListResponse = z.infer<
  typeof inventorySuggestedTransfersListResponseSchema
>;
export type SuggestedTransferListFilter = z.infer<
  typeof suggestedTransferListFilterSchema
>;
